import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { isSoundMuted, toggleSoundMuted } from "../lib/sound.js";
import {
  isMusicMuted,
  toggleMusicMuted,
  startMusic,
  stopMusic,
  getCurrentTrackId,
  cycleTrack,
  TRACKS,
} from "../lib/music.js";

function formatCurrency(cents) {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export default function HUD() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [muted, setMuted] = useState(isSoundMuted());
  const [musicMuted, setMusicMutedState] = useState(isMusicMuted());
  const [trackId, setTrackId] = useState(getCurrentTrackId());

  const level = profile?.level ?? 1;
  const xp = profile?.xp ?? 0;
  const xpForNextLevel = level * 500;
  const xpPct = Math.min(100, Math.round((xp / xpForNextLevel) * 100));
  const netWorthCents = profile?.net_worth_cents ?? 0;
  const streak = profile?.current_streak ?? 0;

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 border-b-2 border-black bg-panel/95 backdrop-blur">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3">
        <button
          onClick={() => navigate("/")}
          className="font-display text-[11px] text-magenta hover:text-cyan sm:text-sm"
        >
          LEVEL UP <span className="text-mist">WEALTH</span>
        </button>

        <div className="flex flex-1 items-center gap-3 sm:flex-none sm:gap-6">
          <div className="min-w-[120px] flex-1 sm:flex-none">
            <div className="mb-1 flex items-center justify-between font-display text-[8px] text-mist sm:text-[9px]">
              <span>LV.{level}</span>
              <span>{xp}/{xpForNextLevel} XP</span>
            </div>
            <div className="h-2.5 w-full border border-black bg-ink sm:w-40">
              <div
                className="h-full bg-gradient-to-r from-magenta to-cyan transition-all duration-500"
                style={{ width: `${xpPct}%` }}
              />
            </div>
          </div>

          {streak > 0 && (
            <div
              className="flex items-center gap-1.5 border-2 border-black bg-ink px-2 py-1.5 shadow-pixel-sm"
              title={`${streak}-day streak`}
            >
              <span>🔥</span>
              <span className="font-display text-[9px] text-mist sm:text-[10px]">{streak}</span>
            </div>
          )}

          <div className="flex items-center gap-1.5 border-2 border-black bg-ink px-2 py-1.5 shadow-pixel-sm">
            <span className="text-gold">$</span>
            <span className="font-display text-[9px] text-gold sm:text-[10px]">
              {formatCurrency(netWorthCents)}
            </span>
          </div>

          <button onClick={() => navigate("/challenges")} className="btn-ghost !px-2 !py-1.5 !text-[8px]" title="Challenges">
            ⚔️
          </button>

          <button onClick={() => navigate("/leaderboard")} className="btn-ghost !px-2 !py-1.5 !text-[8px]" title="Family Leaderboard">
            🏆
          </button>

          <button
            onClick={() => setMuted(toggleSoundMuted())}
            className="btn-ghost !px-2 !py-1.5 !text-[8px]"
            title={muted ? "Unmute sound" : "Mute sound"}
          >
            {muted ? "🔇" : "🔊"}
          </button>

          <button
            onClick={() => {
              const newMuted = toggleMusicMuted();
              setMusicMutedState(newMuted);
              if (newMuted) {
                stopMusic();
              } else {
                startMusic();
              }
            }}
            className="btn-ghost !px-2 !py-1.5 !text-[8px]"
            title={musicMuted ? "Turn on music" : "Turn off music"}
          >
            {musicMuted ? "🔕" : "🎵"}
          </button>

          {!musicMuted && (
            <button
              onClick={() => setTrackId(cycleTrack())}
              className="btn-ghost !px-2 !py-1.5 !text-[8px]"
              title={`Now playing: ${TRACKS.find((t) => t.id === trackId)?.name ?? ""} — tap to skip`}
            >
              ⏭
            </button>
          )}

          <button onClick={handleSignOut} className="btn-ghost !px-2 !py-1.5 !text-[8px]">
            LOG OUT
          </button>
        </div>
      </div>
    </header>
  );
}
